import type { ShippingCard } from '../../lib/api/operatorConsole';
import { formatAmount, formatUsdCents } from './formatMoney';

interface Props {
  cards: ShippingCard[];
  /** Operator currency — refunds were credited back in it. */
  currency: string | null;
  /** Reports a card as unfulfillable; the page owns the confirmation. */
  onReportUnavailable: (card: ShippingCard) => void;
  /** Line id currently being reported, so its button stops taking clicks. */
  reportingCardId: number | null;
  /** Another write on this delivery is in flight (a staging sandbox action). */
  actionsDisabled?: boolean;
}

const COLUMNS = ['Card', 'Owner', 'Locked FMV', 'Round', 'Pack opened', 'Certificate', 'Fulfilment', ''];

const cell = 'px-4 py-3 text-sm text-ui-text';
const mutedCell = 'px-4 py-3 text-sm text-ui-text-secondary';

function cardName(card: ShippingCard): string {
  const graded = [card.grading_company, card.grade].filter(Boolean).join(' ');

  return [card.name ?? `Item #${card.pool_item_id}`, graded].filter(Boolean).join(' · ');
}

// Your own name, or "Shared". Cards owned by another operator are shared as
// far as this console is concerned — the backend never sends whose they are.
function ownerLabel(card: ShippingCard): string {
  return card.owner?.mine ? (card.owner.name ?? 'Yours') : 'Shared';
}

export function ShippingCardsTable({
  cards,
  currency,
  onReportUnavailable,
  reportingCardId,
  actionsDisabled = false,
}: Props) {
  if (cards.length === 0) {
    return (
      <section
        aria-label="Cards"
        className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary"
      >
        No cards are attached to this delivery.
      </section>
    );
  }

  return (
    <section aria-label="Cards" className="rounded-lg border border-ui-border bg-ui-card">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-ui-border">
              {COLUMNS.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="px-4 py-3 text-left text-sm font-semibold text-ui-text"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cards.map((card) => (
              <tr key={card.id} className="border-b border-ui-border last:border-0">
                <td className={cell}>{cardName(card)}</td>
                <td className={card.owner?.mine ? cell : mutedCell}>{ownerLabel(card)}</td>
                {/* Always USD, whatever the operator's own currency is. */}
                <td className={`${cell} font-mono`}>
                  {formatUsdCents(card.locked_fmv_usd_cents)}
                </td>
                <td className={mutedCell}>{card.round_id ? `#${card.round_id}` : '—'}</td>
                <td className={mutedCell}>{card.pack_title ?? '—'}</td>
                <td className={`${mutedCell} font-mono`}>{card.certificate_number ?? '—'}</td>
                <td className={cell}>
                  {card.refund ? (
                    <>
                      <span style={{ color: 'var(--co-error)' }}>Refunded</span>
                      <div className="text-[11px] text-ui-text-secondary">
                        {formatAmount(
                          card.refund.amount_cents === null
                            ? null
                            : card.refund.amount_cents / 100,
                          currency,
                        )}
                        {card.refund.at ? ` · ${new Date(card.refund.at).toLocaleDateString()}` : ''}
                      </div>
                    </>
                  ) : (
                    <span className="capitalize">{card.settle_status}</span>
                  )}
                </td>
                <td className={`${cell} text-right`}>
                  {/* The backend decides who gets a button — a card that
                      already left, or a delivery past our custody, simply has
                      the flag off. */}
                  {card.can_report_unavailable && (
                    <button
                      type="button"
                      onClick={() => onReportUnavailable(card)}
                      disabled={reportingCardId !== null || actionsDisabled}
                      aria-label={`Can't ship ${cardName(card)}`}
                      className="rounded-lg border border-ui-border px-3 py-1 text-[12px] font-medium text-ui-text hover:border-[var(--co-error)] hover:text-[var(--co-error)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reportingCardId === card.id ? 'Reporting…' : "🚫 Can't ship"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
